import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, CardHeader, CardTitle, CardContent } from '../components';
import { RefreshCw, Download, Share2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';

export const InvestigationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'resumo' | 'fontes' | 'resultados' | 'relatorio'>('resumo');

  useEffect(() => {
    // TODO: Carregar investigação do backend
    setIsLoading(false);
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-white">Carregando investigação...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Investigação #{id}</h1>
          <div className="flex gap-4 text-sm text-gray-400">
            <span>Tipo: Pessoa Física</span>
            <span>Status: Em Andamento</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
          <Button variant="secondary" size="sm" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Exportar
          </Button>
          <Button variant="secondary" size="sm" className="flex items-center gap-2">
            <Share2 className="w-4 h-4" />
            Compartilhar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-800 flex gap-8">
        {(['resumo', 'fontes', 'resultados', 'relatorio'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-3 border-b-2 transition-colors capitalize ${
              activeTab === tab
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Resumo Tab */}
      {activeTab === 'resumo' && (
        <div className="space-y-6">
          {/* Progresso */}
          <Card>
            <CardHeader>
              <CardTitle>Progresso da Investigação</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Fontes Consultadas</span>
                  <span className="text-white font-medium">2 de 4</span>
                </div>
                <div className="w-full bg-gray-800 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: '50%' }}></div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-gray-800">
                <div>
                  <p className="text-gray-400 text-sm">Sucesso</p>
                  <p className="text-2xl font-bold text-green-500">2</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Pendentes</p>
                  <p className="text-2xl font-bold text-amber-500">2</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Erros</p>
                  <p className="text-2xl font-bold text-red-500">0</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Resultados</p>
                  <p className="text-2xl font-bold text-blue-500">15</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Identificadores */}
          <Card>
            <CardHeader>
              <CardTitle>Identificadores Pesquisados</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {[
                { type: 'CPF', value: '123.456.789-00' },
                { type: 'E-mail', value: 'pessoa@example.com' },
              ].map((id, idx) => (
                <div key={idx} className="flex justify-between p-3 bg-gray-800 rounded">
                  <span className="text-gray-300">{id.type}</span>
                  <span className="text-white font-mono">{id.value}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Fontes Tab */}
      {activeTab === 'fontes' && (
        <div className="space-y-3">
          {[
            { name: 'BrasilAPI', status: 'sucesso', duration: '245ms', records: 5 },
            { name: 'crt.sh', status: 'sucesso', duration: '1,230ms', records: 10 },
            { name: 'DataJud', status: 'pendente', duration: '-', records: 0 },
            { name: 'Portal Transparência', status: 'pendente', duration: '-', records: 0 },
          ].map((source, idx) => (
            <Card key={idx}>
              <CardContent className="py-6 flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-white">{source.name}</h4>
                  <p className="text-sm text-gray-400">
                    {source.records} registro{source.records !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-400">{source.duration}</span>
                  {source.status === 'sucesso' && (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  )}
                  {source.status === 'pendente' && (
                    <Clock className="w-5 h-5 text-amber-500" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Resultados Tab */}
      {activeTab === 'resultados' && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">
              Os resultados detalhados aparecerão aqui conforme as fontes forem processadas.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Relatório Tab */}
      {activeTab === 'relatorio' && (
        <Card>
          <CardHeader>
            <CardTitle>Gerar Relatório</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-300 text-sm">
              O relatório consolidado com todas as evidências e fontes consultadas será
              disponibilizado ao final da investigação.
            </p>
            <div className="grid grid-cols-3 gap-3">
              <Button variant="secondary" className="flex items-center justify-center gap-2">
                <Download className="w-4 h-4" />
                PDF
              </Button>
              <Button variant="secondary" className="flex items-center justify-center gap-2">
                <Download className="w-4 h-4" />
                CSV
              </Button>
              <Button variant="secondary" className="flex items-center justify-center gap-2">
                <Download className="w-4 h-4" />
                JSON
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
